/* eslint-disable react/jsx-props-no-spreading */
import { FC, useEffect } from 'react'
import ReactGA from 'react-ga4'
import type { ITemplate } from '../interfaces/templates'
import Default from './Default'

/**
 * Order Confirmation screen.
 *
 * There is no route for it in code: cinnamon builds its routes from the SCS
 * config's own `routes` array, and `defaultConfig` ships none for this — so
 * `CinnamonPaths.ORDER_CONFIRMATION` is the path an author is expected to
 * register, not one that exists. **Nothing navigates here yet either**, which
 * means the screen is unreachable until a storefront authors both the
 * container and a `pushView` onto it.
 *
 * Thin like `OrderDetail` and `OrderHistory` beside it: fire analytics,
 * delegate rendering to `Default`. `OrderConfirmationPanel`, its state copy
 * and its custom slots are authored in SCS config and resolve through
 * `importRoute`, so no layout belongs here — and the props the config cannot
 * express are injected in `useCinnamonProps`, not passed down.
 *
 * **It deliberately fetches nothing.** The other two screens call a hook on
 * mount because they are reads. This one is the tail of a *write* that
 * `useOrderConfirmation` already owns, started from the cart CTA through
 * `useCreateOrder`: the mutation, the confirmation, the poll and the stephook
 * all run there, and a settled order is in `ordersSlice.created` before this
 * screen mounts.
 *
 * Note this screen already *has* an instance of that hook — every container
 * does, via `Default` → `EventManager` → `useCreateOrderHook` — and it is
 * inert because nothing here calls `createAndConfirmOrder`. What calling the
 * hook in this file would add is a second **chain**: another instance with its
 * own poll timer and request token in `useRef`, writing the same slice on its
 * own schedule. That is COM-741's defect class, and the reason COM-779 put the
 * detail refresh behind a nonce rather than a second hook. The nonce work for
 * this screen should drive the instance `EventManager` already mounts.
 *
 * Two consequences worth naming, both recorded in `.claude/CLAUDE.md`:
 *
 * - **Reaching this route kills the poll.** `usePushView` is `navigate()` and
 *   `<Routes>` mounts one container at a time, so arriving here unmounts the
 *   cart's `EventManager` and its cleanup stops the timer. Redux survives, so
 *   a *settled* outcome renders correctly and the synchronous path is fine; an
 *   attempt still `confirming` never settles, and `unknown` — the only state
 *   offering a refresh — becomes unreachable.
 * - **A direct link shows a spinner over no order.** `creationStatus` stays
 *   `idle`, which maps to the panel's in-flight `creating`. Whether that is a
 *   dead end depends on config: the exit renders in every status, but only
 *   when `returnToConversationLabel` is authored, and this repo ships no
 *   `texts.orderConfirmation.*` default. A route guard is the fix and it
 *   belongs here; see `PANEL_STATUS_BY_SLICE_STATUS` in
 *   `utils/orderConfirmationPanel.ts`.
 */
const OrderConfirmation: FC<ITemplate> = ({ ...props }) => {
  useEffect(() => {
    ReactGA.send({ hitType: 'pageview', page: `/${props.container.uid}` })
    window.fbq('track', 'PageView', { page: `/${props.container.uid}` })
  }, [])

  return <Default {...props} />
}

export default OrderConfirmation
